import { useEffect, useRef, useState } from "react";
import "../../../scss/onboarding.scss";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";

import Logo from "../../../assets/logo.png";
import RightImage from "../../../assets/landing/onboard-2.png";
import { getCredentials } from "../../../shared/utils";
import { authActions } from "../../../redux/actions/onboarding";
import { AlertMsg } from "../../../shared/components/alert-msg";
import { authConstants } from "../../../redux/action-constants/onboarding.constants";
import { LoadingItem } from "../../../shared/components/loading";
import { AuthDrawerSheet, VerifyEmailOtp } from "../../../shared/components/otp";
import { VendorToast } from "../../../shared/components/vendor-toast";

const confirmAccountEmailAction = async ({ pageProps, action }: any) => {
  await pageProps.ConfirmAccountEmail(action);
};

const handleLogin = async ({ pageProps, payload }: any) => {
  await pageProps.LoginAction(payload);
};

const requestAccountActivationCode = async ({ pageProps, payload }: any) => {
  return pageProps.RequestAccountActivationCode(payload);
};

const activateAccountWithCode = async ({ pageProps, payload }: any) => {
  return pageProps.ActivateAccountWithCode(payload);
};

const VerificationContent = ({ pageProps }: any) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const email = params.get("email") || "";
  const token = params.get("token") || "";
  const shouldUseOtp = Boolean(email && !token);
  const pagePropsRef = useRef(pageProps);
  const initialEmailRef = useRef(email);
  const initialTokenRef = useRef(token);
  const requestedOtpRef = useRef<string>("");
  const [otpEmail, setOtpEmail] = useState<string>(email);
  const [isOtpDrawerOpen, setIsOtpDrawerOpen] = useState<boolean>(shouldUseOtp);
  const [isEmailVerifiedByOtp, setIsEmailVerifiedByOtp] =
    useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>("");

  const handleUserInitialLogin = async () => {
    const credentials = getCredentials();
    if (!credentials) return;

    const payload: any = {
      username: credentials.email,
      password: credentials.password,
      grant_type: "password",
      scope: "api1.read IdentityServerApi",
      client_secret: "SuperSecretPassword",
      client_id: "oauthClient",
    };

    let formBody: any = [];
    for (const property in payload) {
      const encodedKey = encodeURIComponent(property);
      const encodedValue = encodeURIComponent(payload[property]);
      formBody.push(encodedKey + "=" + encodedValue);
    }
    formBody = formBody.join("&");

    await handleLogin({ pageProps, payload: formBody }).then(() => {
      localStorage.removeItem("user_credentials");
    });
  };

  useEffect(() => {
    const initialEmail = initialEmailRef.current;
    const initialToken = initialTokenRef.current;
    const currentPageProps = pagePropsRef.current;

    handleLogin({
      pageProps: currentPageProps,
      payload: "CLEAR",
    });

    if (initialToken) {
      confirmAccountEmailAction({
        pageProps: currentPageProps,
        action: {
          email: initialEmail,
          token: initialToken,
        },
      });
      return;
    }

    if (initialEmail) {
      setOtpEmail(initialEmail);
      setIsOtpDrawerOpen(true);
      setToastMessage(`Verify ${initialEmail} to continue.`);

      if (requestedOtpRef.current !== initialEmail) {
        requestedOtpRef.current = initialEmail;
        requestAccountActivationCode({
          pageProps: currentPageProps,
          payload: initialEmail,
        });
      }
    }
  }, []);

  useEffect(() => {
    if (!toastMessage) return;

    const toastTimer = window.setTimeout(() => {
      setToastMessage("");
    }, 6000);

    return () => window.clearTimeout(toastTimer);
  }, [toastMessage]);

  const isEmailVerified =
    isEmailVerifiedByOtp ||
    pageProps?.confirmEmailRequest?.request_status ===
      authConstants.CONFIRM_EMAIL_SUCCESS;
  const isConfirmingEmail =
    token &&
    pageProps?.confirmEmailRequest?.request_status ===
      authConstants.CONFIRM_EMAIL_PENDING;
  const hasTokenVerificationError =
    token &&
    pageProps?.confirmEmailRequest?.request_status ===
      authConstants.CONFIRM_EMAIL_FAILURE;

  return (
    <div className="email-verify-content">
      <VendorToast message={toastMessage} type="success" />
      <AuthDrawerSheet
        open={isOtpDrawerOpen}
        onClose={() => setIsOtpDrawerOpen(false)}
      >
        <VerifyEmailOtp
          email={otpEmail}
          onVerify={(payload) =>
            activateAccountWithCode({ pageProps, payload })
          }
          onSuccess={() => {
            setIsEmailVerifiedByOtp(true);
          }}
          onResend={() =>
            requestAccountActivationCode({ pageProps, payload: otpEmail })
          }
          onLogin={() => {
            setIsOtpDrawerOpen(false);
            window.location.href = "/log-in";
          }}
        />
      </AuthDrawerSheet>

      <div className="left-content">
        {isConfirmingEmail ? (
          <LoadingItem />
        ) : hasTokenVerificationError ? (
          <AlertMsg
            type="error"
            message={pageProps?.confirmEmailRequest?.request_data?.error}
          />
        ) : isEmailVerified ? (
          <div className="left-inner-content">
            <div className="text-content">
              <h3>Email address confirmed</h3>
              <p>Your email address is now confirmed.</p>
            </div>
            {pageProps?.loginRequest?.request_status ===
              authConstants.LOGIN_USER_PENDING && <LoadingItem />}
            {pageProps?.loginRequest?.request_status ===
              authConstants.LOGIN_USER_FAILURE && (
              <AlertMsg
                type="error"
                message={pageProps?.loginRequest?.request_data?.error}
              />
            )}
            <button
              onClick={handleUserInitialLogin}
              disabled={
                !getCredentials() ||
                pageProps?.loginRequest?.request_status ===
                  authConstants.LOGIN_USER_PENDING
              }
            >
              Continue to your vendor dashboard
            </button>
          </div>
        ) : email ? (
          <div className="left-inner-content">
            <div className="text-content">
              <h3>Verify your email address</h3>
              <p>Enter the OTP sent to your email to verify your account.</p>
            </div>
            <button type="button" onClick={() => setIsOtpDrawerOpen(true)}>
              Enter OTP
            </button>
          </div>
        ) : (
          <AlertMsg
            type="error"
            message="Email verification details are missing."
          />
        )}
      </div>

      <div className="right-content">
        <img src={RightImage} alt="Vendors" />
      </div>
    </div>
  );
};

const OnboardVerification = (pageProps: any) => {
  return (
    <div className="verify-wrap">
      <div className="verify-head">
        <div className="logo-head">
          <img src={Logo} alt="" />
        </div>
      </div>
      <VerificationContent pageProps={pageProps} />
    </div>
  );
};

const mapDispatchToProps = {
  ConfirmAccountEmail: authActions.ConfirmAccountEmail,
  GetOtpForVendorPhone: authActions.GetOtpForVendorPhone,
  ConfirmAccountPhone: authActions.ConfirmAccountPhone,
  logoutAction: authActions.Logout,
  LoginAction: authActions.Login,
  ActivateAccountWithCode: authActions.ActivateAccountWithCode,
  RequestAccountActivationCode: authActions.RequestAccountActivationCode,
};

const mapStateToProps = (state: any) => ({
  confirmEmailRequest: state.onboardingOutboundReducers.confirmEmailReducer,
  confirmAccountPhoneRequest:
    state.onboardingOutboundReducers.confirmAccountPhoneReducer,
  getOtpForVendorPhoneRequest:
    state.onboardingOutboundReducers.getOtpForVendorPhoneReducer,
  loginRequest: state.onboardingOutboundReducers.loginReducer,
  activateAccountWithCodeRequest:
    state.onboardingOutboundReducers.activateAccountWithCodeReducer,
  requestAccountActivationCodeRequest:
    state.onboardingOutboundReducers.requestAccountActivationCodeReducer,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(OnboardVerification);
